/**
 * Local pending timestamps for blocked→known latency (G12). Kept free of page-only imports: the
 * service worker uses it to note push received/shown/opened; the page reads and clears it.
 */
import { idb } from './idb.js'
import type { Stamps } from './latency.js'

const PENDING_KEY = 'latency/pending'
const PENDING_TTL_MS = 24 * 3_600_000
const PENDING_MAX = 100
type Pending = Record<string, { at: number; stamps: Stamps }>

/** Merge timestamps for `id` into the local pending record (older entries pruned). */
export async function noteStamps(id: string, stamps: Stamps, now = Date.now()): Promise<void> {
  if (!id) return
  await idb.update<Pending>(PENDING_KEY, (cur) => {
    const next: Pending = {}
    const rows = Object.entries(cur ?? {})
      .filter(([, v]) => now - v.at < PENDING_TTL_MS)
      .sort((a, b) => b[1].at - a[1].at)
      .slice(0, PENDING_MAX - 1)
    for (const [k, v] of rows) next[k] = v
    next[id] = { at: now, stamps: { ...(next[id]?.stamps ?? {}), ...stamps } }
    return next
  })
}

export async function readPending(): Promise<Map<string, Stamps>> {
  const cur = (await idb.get<Pending>(PENDING_KEY)) ?? {}
  return new Map(Object.entries(cur).map(([k, v]) => [k, v.stamps]))
}

export async function clearPending(ids: readonly string[]): Promise<void> {
  if (!ids.length) return
  await idb.update<Pending>(PENDING_KEY, (cur) => {
    const next = { ...(cur ?? {}) }
    for (const id of ids) delete next[id]
    return next
  })
}
